<template>
	<button type="button" v-if="available" @click="openFilePicker($event)" class="" :disabled="!pickerState.loaded || connecting">
		<img src="/images/icons/one-drive.svg" class="w-8 h-8" alt="One-Drive icon"/>
		<span class="hidden sm:inline-block">

			<template v-if="connecting || fetchingOauthTokenCompleted">
				<!-- connecting, or successfully connected but briefly showing spinner for UX purposes -->
				<span class="loading loading-spinner"></span>
			</template>

			<template v-else-if="pickerState.connected">
				{{ label }}
			</template>

			<template v-else>
				{{ $t('Connect to OneDrive' )}}
			</template>
		</span>

		<span v-if="!pickerState.loaded">
			<span class="loading loading-spinner"></span>
		</span>
	</button>
</template>

<script lang="ts">

// Manual: https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/?view=odsp-graph-online
//
// Two flavours of the picker exist:
//  - Personal Microsoft accounts use the consumer picker on onedrive.live.com.
//  - Work/school (Entra ID) accounts use the OneDrive for Business picker, hosted on the tenant's own
//    SharePoint domain. Only this one can browse SharePoint document libraries (including Teams' files).
// Which one we use is decided by the tenant of the signed in Microsoft account. The backend stores the tenant
// the user logged in with (me.microsoft_tenant_id), which lets us pick the right authority and sign in silently.
//
// The Azure app registration needs, besides personal + organisational accounts as supported account types:
//  - Microsoft Graph (delegated): User.Read, Files.Read.All
//  - SharePoint (delegated): MyFiles.Read, AllSites.Read

import {FileExternal} from "@/models/FileExternal.model";
import config from "@/config/app.config";
import {mapStores} from "pinia";
import {useUsersStore} from "@/stores/Users.store";
import axios from "axios";
import {v4 as uuidv4} from 'uuid';
import {reactive} from "vue";
import SmaSyLocalStorage from "@/utils/SmaSyLocalStorage.util";

// Tenant id Entra ID uses for all personal Microsoft accounts
const CONSUMER_TENANT_ID = "9188040d-6c67-4c5b-b112-36a304b66dad";
const AUTHORITY_BASE_URL = "https://login.microsoftonline.com/";

const CONSUMER_PICKER_URL = "https://onedrive.live.com/picker";
const CONSUMER_SCOPES = ["OneDrive.ReadWrite"];

// We ask for explicit scopes rather than "/.default": for a user who only consented to part of the app's
// permissions, "/.default" silently returns a token without the missing ones instead of asking for consent.
// Files.Read.All exists for both work/school and personal accounts.
const GRAPH_SCOPES = ["User.Read", "Files.Read.All"];

// The backend requires a mime type for every external file. This is what we fall back to when the metadata
// lookup fails, or the item is neither a regular file nor a folder (e.g. a OneNote notebook).
const UNKNOWN_MIME_TYPE = "application/octet-stream";

const STORAGE_KEY_ACCOUNT = 'onedrive_picker_account';
const STORAGE_KEY_HOST = 'onedrive_picker_host';

// the options we pass to the picker page through the querystring
function buildOneDriveFilePickerOptions(accepts: Array<'files' | 'folders'>, isPersonal: boolean, hasOneDrive: boolean) {
	const foldersOnly = accepts.includes('folders') && !accepts.includes('files');
	const filesOnly   = accepts.includes('files')   && !accepts.includes('folders');

	/**
	 * OneDrive picker mode:
	 *  - "files"   → files only
	 *  - "folders" → folders only
	 *  - "all"     → both files and folders
	 * @see https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/
	 */
	const mode = foldersOnly ? "folders" : filesOnly ? "files" : "all";

	// The business picker can also show files shared with the user and SharePoint / Teams libraries.
	const pivots = isPersonal
		? { oneDrive: true, recent: true }
		: { oneDrive: hasOneDrive, recent: true, shared: true, sharedLibraries: true };

	// Work/school users without a OneDrive (no licence) start in the document library of the organisation's
	// root SharePoint site instead. The picker needs an actual library to open; a bare site does not exist as an item.
	const entry = hasOneDrive
		? { oneDrive: { files: {} } }
		: { sharePoint: { byPath: { web: pickerHost, list: pickerLibrary, fallbackToRoot: true } } };

	return {
		sdk: "8.0",
		entry,
		authentication: {},
		messaging: {
			origin: window.location.protocol + "//" + window.location.host,
			channelId: uuidv4()
		},
		typesAndSources: {
			mode,
			pivots,
		},
		selection: {
			mode: "multiple",
			typesAndSources: true,
		},
	};
}

/**
 * SharePoint tokens are requested per host ("resource"), e.g. https://school-my.sharepoint.com for OneDrive
 * and https://school.sharepoint.com for team sites.
 */
function sharePointScopes(resource: string) {
	const origin = new URL(resource).origin;
	return [origin + "/AllSites.Read", origin + "/MyFiles.Read"];
}

/**
 * The scopes for a token the business picker asks for (its "authenticate" command): mostly a SharePoint
 * token for a specific host (command.resource), but it can ask for a Microsoft Graph token as well.
 */
function pickerCommandScopes(command: { type?: string, resource: string }) {
	return command.type === "Graph" ? GRAPH_SCOPES : sharePointScopes(command.resource);
}

function authority(tenantId?: string | null) {
	if (!tenantId) {
		return AUTHORITY_BASE_URL + "common";
	}

	return AUTHORITY_BASE_URL + (tenantId === CONSUMER_TENANT_ID ? "consumers" : tenantId);
}

function isPersonalAccount(account: any) {
	return account.tenantId === CONSUMER_TENANT_ID;
}

// The Microsoft account has neither a OneDrive nor access to the organisation's SharePoint (typically: no licence).
class NoFileStorageError extends Error {}

let oneDriveLibrariesLoadedPromise: Promise<void> | null = null;
let popupWindow: Window | null = null;
let msApp = null;

// The Microsoft account we are working with, and (for work/school accounts) the SharePoint host of its OneDrive.
let account = null;
let pickerHost: string | null = null;
let pickerHasOneDrive = true;
let pickerLibrary: string | null = null;

// Shared between all picker buttons on the page
const pickerState = reactive({
	loaded: false,
	connected: false,
});

export default {

	computed: {
		...mapStores(useUsersStore),

		available() {
			if (!config.AZURE?.client_id) {
				return false;
			}

			return true;
		},
	},

	props: {
		label: {
			type: String,
			required: false,
		},
		/**
		 * Controls which entity types can be selected.
		 * Accepted values: 'files', 'folders' or both.
		 * Defaults to ['files'] (files only).
		 *
		 * @example :accepts="['files']"            // files only (default)
		 * @example :accepts="['folders']"          // folders only
		 * @example :accepts="['files', 'folders']" // both
		 */
		accepts: {
			type: Array as () => Array<'files' | 'folders'>,
			default: () => ['files'],
			validator: (value: string[]) => value.every(v => ['files', 'folders'].includes(v)),
		},
	},

	data() {
		return {
			pickerState,
			port: null,
			messageHandler: null,
			connecting: false,
			fetchingOauthTokenCompleted: false,
		};
	},

	emits: [
		'filesSelected'
	],

	created() {
		this.loadOneDriveApi();
	},

	beforeUnmount() {
		if (this.messageHandler) {
			window.removeEventListener("message", this.messageHandler);
		}
	},

	methods: {

		loadOneDriveApi() {

			if (!oneDriveLibrariesLoadedPromise) {

				oneDriveLibrariesLoadedPromise = (async () => {

					await this.loadScripts([
						"https://alcdn.msauth.net/browser/2.19.0/js/msal-browser.min.js"
					]);

					msApp = new msal.PublicClientApplication({
						auth: {
							authority: authority(null),
							clientId: config.AZURE.client_id,
							redirectUri: window.location.protocol + "//" + window.location.host,
						},
						// MSAL keeps its cache in sessionStorage (the default), which FilePickerCredentials.clear()
						// wipes on logout.
					});

					// Try to get everything the picker needs without user interaction
					try {
						await this.connect(false);
					} catch (e) {
						// Not connected (yet); the user can connect by clicking the button.
					}

				})().finally(() => {
					pickerState.loaded = true;
				});
			}

			return oneDriveLibrariesLoadedPromise;
		},

		loadScripts(scripts: string[]) {
			const promises = [];

			scripts.forEach((script: string) => {

				promises.push(new Promise<void>((resolve, reject) => {

					let scriptElement = document.createElement("script");
					scriptElement.setAttribute("type", "text/javascript");
					scriptElement.setAttribute("src", script);

					scriptElement.onload = () => {
						resolve();
					}
					scriptElement.onerror = () => {
						reject(new Error('Could not load ' + script));
					}

					document.head.appendChild(scriptElement);

				}));

			});

			return Promise.all(promises);
		},

		/**
		 * Make sure we have a Microsoft account and everything the picker needs for it.
		 * Throws if that is not possible (without interaction, if interactive is false).
		 */
		async connect(interactive: boolean) {

			const me = this.usersStore.me;

			if (!account && !interactive) {
				account = this.findCachedAccount();
			}

			if (!interactive && !account && me?.microsoft_tenant_id) {
				// The user signed in to SmartSymbols with Microsoft, so there's likely still a Microsoft session.
				try {
					const response = await msApp.ssoSilent({
						scopes: this.loginScopes(me.microsoft_tenant_id),
						loginHint: me.email,
						authority: authority(me.microsoft_tenant_id),
					});
					this.setAccount(response.account);
				} catch (e) {
					// No usable session (or third party cookies are blocked); fall through to interactive login
				}
			}

			if (!account) {
				if (!interactive) {
					throw new Error('Not signed in to Microsoft');
				}

				const tenantId = me?.microsoft_tenant_id;
				// Always let the user pick the account, instead of silently reusing whichever
				// Microsoft session happens to be active in this browser (shared computers).
				const response = await msApp.loginPopup({
					scopes: this.loginScopes(tenantId),
					loginHint: me?.email,
					authority: authority(tenantId),
					prompt: "select_account",
				});
				this.setAccount(response.account);
			}

			if (!isPersonalAccount(account) && !pickerHost) {
				const location = await this.resolvePickerHost(interactive);
				pickerHost = location.host;
				pickerHasOneDrive = location.hasOneDrive;
				pickerLibrary = location.library ?? null;
			}

			// Check that we can get a token for the picker
			await this.getPickerToken(interactive);

			pickerState.connected = true;
		},

		/**
		 * Find the Microsoft account that belongs to the current user in MSAL's cache.
		 * We don't just take any cached account: on shared (school) computers, that might be someone else.
		 */
		findCachedAccount() {
			const me = this.usersStore.me;
			const accounts = msApp.getAllAccounts();

			const storedHomeAccountId = me ? SmaSyLocalStorage.getCurrentUserItem(STORAGE_KEY_ACCOUNT) : null;

			return accounts.find(a => storedHomeAccountId && a.homeAccountId === storedHomeAccountId)
				?? accounts.find(a => me?.microsoft_id && a.localAccountId === me.microsoft_id)
				?? accounts.find(a => me?.email && a.username?.toLowerCase() === me.email.toLowerCase())
				?? null;
		},

		setAccount(newAccount) {
			if (account?.homeAccountId !== newAccount.homeAccountId) {
				pickerHost = null;
			}

			account = newAccount;
			msApp.setActiveAccount(account);

			if (this.usersStore.me) {
				SmaSyLocalStorage.setCurrentUserItem(STORAGE_KEY_ACCOUNT, account.homeAccountId);
			}
		},

		loginScopes(tenantId?: string | null) {
			// If the account type is not known yet, the Graph scopes work for both.
			return tenantId === CONSUMER_TENANT_ID ? CONSUMER_SCOPES : GRAPH_SCOPES;
		},

		async acquireToken(scopes: string[], interactive: boolean): Promise<string> {
			const request = {
				scopes,
				account,
				authority: authority(account.tenantId),
			};

			try {
				const response = await msApp.acquireTokenSilent(request);
				return response.accessToken;
			} catch (e) {
				if (!interactive) {
					throw e;
				}

				const response = await msApp.acquireTokenPopup({ ...request, loginHint: account.username });
				return response.accessToken;
			}
		},

		/**
		 * The business picker is hosted on the SharePoint domain of the user's OneDrive
		 * (e.g. https://school-my.sharepoint.com), which we look up once through Microsoft Graph.
		 * Users without a OneDrive get the organisation's root SharePoint site (https://school.sharepoint.com).
		 */
		async resolvePickerHost(interactive: boolean): Promise<{ host: string, hasOneDrive: boolean, library?: string }> {
			const cacheKey = STORAGE_KEY_HOST + '_' + account.tenantId;
			const cached = SmaSyLocalStorage.getCurrentUserItem(cacheKey);
			if (cached?.host && (cached.hasOneDrive || cached.library)) {
				return cached;
			}

			const token = await this.acquireToken(GRAPH_SCOPES, interactive);
			const graphClient = axios.create({
				baseURL: "https://graph.microsoft.com/v1.0",
				headers: { "Authorization": "Bearer " + token },
			});

			let location: { host: string, hasOneDrive: boolean, library?: string };
			try {
				const response = await graphClient.get("/me/drive?$select=webUrl");
				location = { host: new URL(response.data.webUrl).origin, hasOneDrive: true };
			} catch (e) {
				if (e?.response?.status !== 404) {
					throw e;
				}

				// No OneDrive (yet). Not cached for long, as the user might get one later.
				// The default document library of the root site; its url differs per tenant language.
				let response;
				try {
					response = await graphClient.get("/sites/root/drive?$select=webUrl");
				} catch (e) {
					if ([403, 404].includes(e?.response?.status)) {
						throw new NoFileStorageError(
							`${account.username} (tenant ${account.tenantId}) has no OneDrive and no access to the root SharePoint site`
						);
					}
					throw e;
				}

				const libraryUrl = decodeURI(response.data.webUrl);
				location = { host: new URL(libraryUrl).origin, hasOneDrive: false, library: libraryUrl };
			}

			SmaSyLocalStorage.setCurrentUserItem(cacheKey, location, location.hasOneDrive ? null : 60 * 60);

			return location;
		},

		getPickerToken(interactive: boolean): Promise<string> {
			if (isPersonalAccount(account)) {
				return this.acquireToken(CONSUMER_SCOPES, interactive);
			}

			return this.acquireToken(sharePointScopes(pickerHost), interactive);
		},

		async openFilePicker(event) {

			event.preventDefault();

			if (!oneDriveLibrariesLoadedPromise) {
				console.error('OneDrive libraries not loaded (yet?)');
				return;
			}

			if (!pickerState.connected) {
				// Connecting may open login / consent popups. We don't chain the picker popup to that,
				// as it would be blocked by the browser; the user clicks again once connected.
				await this.connectInteractively();
				return;
			}

			await this.launchPicker();
		},

		async connectInteractively() {
			this.connecting = true;
			try {
				await this.connect(true);

				this.fetchingOauthTokenCompleted = true;
				setTimeout(() => {
					this.fetchingOauthTokenCompleted = false;
				}, 2000);

			} catch (e) {
				console.error(e);

				let text = this.$t('Could not connect to OneDrive');
				if (e instanceof NoFileStorageError) {
					text = this.$t('The Microsoft account {account} has no OneDrive or SharePoint. Connect with another account, or ask your IT administrator for a license.', { account: account?.username });
				} else if (['popup_window_error', 'empty_window_error'].includes(e?.errorCode)) {
					// A follow-up consent window was blocked, as it did not directly follow a click.
					// Keep the account, so the next click continues where we left off.
					text = this.$t('Your browser blocked a Microsoft window. Click again to continue.');
					this.$notify({ text, type: 'error' });
					return;
				}

				// Don't hold on to the account that failed (it might be the wrong one);
				// the next click shows the Microsoft account chooser again.
				account = null;

				this.$notify({ text, type: 'error' });
			} finally {
				this.connecting = false;
			}
		},

		async launchPicker() {

			if (popupWindow) {
				popupWindow.close();
			}

			// Open the window before doing anything asynchronous, so it is not blocked as a popup.
			popupWindow = window.open("", "Picker", "width=800,height=600");
			if (!popupWindow) {
				return;
			}

			let token: string;
			try {
				token = await this.getPickerToken(false);
			} catch (e) {
				// Session expired or consent was revoked; let the user reconnect.
				popupWindow.close();
				popupWindow = null;
				pickerState.connected = false;
				account = null;
				return;
			}

			const isPersonal = isPersonalAccount(account);
			const oneDriveFilePickerOptions = buildOneDriveFilePickerOptions(this.accepts, isPersonal, isPersonal || pickerHasOneDrive);

			const queryString = new URLSearchParams({
				filePicker: JSON.stringify(oneDriveFilePickerOptions),
			});

			const url = isPersonal
				? `${CONSUMER_PICKER_URL}?${queryString}`
				: `${pickerHost}/_layouts/15/FilePicker.aspx?${queryString}`;

			const form = popupWindow.document.createElement("form");
			form.setAttribute("action", url);
			form.setAttribute("method", "POST");
			popupWindow.document.body.append(form);

			const input = popupWindow.document.createElement("input");
			input.setAttribute("type", "hidden")
			input.setAttribute("name", "access_token");
			input.setAttribute("value", token);
			form.appendChild(input);

			form.submit();

			if (this.messageHandler) {
				window.removeEventListener("message", this.messageHandler);
			}

			this.messageHandler = (event) => {

				if (event.source && event.source === popupWindow) {

					const message = event.data;

					if (
						message.type === "initialize" &&
						message.channelId === oneDriveFilePickerOptions.messaging.channelId
					) {

						this.port = event.ports[0];

						this.port.addEventListener("message", this.messageListener);

						this.port.start();

						this.port.postMessage({
							type: "activate",
						});
					}
				}
			};

			window.addEventListener("message", this.messageHandler);
		},

		async messageListener(message) {
			switch (message.data.type) {

				case "notification":
					//console.log('notification: ', message.data);
					break;

				case "command":

					this.port.postMessage({
						type: "acknowledge",
						id: message.data.id,
					});

					const command = message.data.data;

					switch (command.command) {

						case "authenticate":

							try {
								// The business picker asks for a token per SharePoint host (command.resource), or for Microsoft Graph
								const token = isPersonalAccount(account)
									? await this.acquireToken(CONSUMER_SCOPES, true)
									: await this.acquireToken(pickerCommandScopes(command), true);

								this.port.postMessage({
									type: "result",
									id: message.data.id,
									data: {
										result: "token",
										token,
									}
								});

							} catch (e) {
								console.error(`Could not get auth token for command: ${JSON.stringify(command)}`, e);

								this.port.postMessage({
									type: "result",
									id: message.data.id,
									data: {
										result: "error",
										error: {
											code: "unableToObtainToken",
											message: e?.message ?? String(e),
										},
									},
								});
							}

							break;

						case "close":

							popupWindow.close();
							break;

						case "pick":

							await this.filesPicked(command.items);

							this.port.postMessage({
								type: "result",
								id: message.data.id,
								data: {
									result: "success",
								},
							});

							popupWindow.close();
							break;

						default:

							console.warn(`Unsupported command: ${JSON.stringify(command)}`);

							this.port.postMessage({
								type: "result",
								id: message.data.id,
								data: {
									result: "error",
									error: {
										code: "unsupportedCommand",
										message: command.command
									},
									isExpected: true,
								},
							});
							break;
					}

					break;
			}
		},

		async filesPicked(files: {
			// File picker typing taken from https://github.com/OneDrive/samples/tree/master/samples/file-picking/sdk-pnptimeline
			"@sharePoint.embedUrl": string;
			"@sharePoint.endpoint": string;
			"@sharePoint.listUrl": string;
			folder?: any;
			id: string;
			name: string;
			parentReference: {
				driveId: string;
				sharepointIds: {
					listId: string;
					siteId: string;
					siteUrl: string;
					webId: string;
				}
			}
			sharepointIds: {
				listId: string;
				listItemId: string;
				listItemUniqueId: string;
				siteId: string;
				siteUrl: string;
				webId: string;
			}
			size: number;
			webDavUrl: string;
			webUrl: string;
		}[]) {

			const promises = files.map(
				async (file) => {

					let externalFile = new FileExternal();
					externalFile.service = "onedrive";
					externalFile.name = file.name;
					externalFile.url = file.webUrl;
					externalFile.mimeType = file.folder
						// THIS MIMETYPE DOES NOT EXIST (officially)
						// But it makes sense to name it like this.
						? 'application/vnd.microsoft-365.folder'
						: UNKNOWN_MIME_TYPE;
					externalFile.size = file.size;

					try {
						externalFile.mimeType = await this.fetchMimeType(file) ?? externalFile.mimeType;
					} catch (e) {
						// Keep the file with a generic mime type rather than dropping the selection
						console.error(`Could not fetch metadata for ${file.name}`, e);
					}

					return externalFile;
				}
			);

			const externalFiles = await Promise.all(promises);
			this.$emit('filesSelected', externalFiles);
		},

		async fetchMimeType(file): Promise<string | null> {
			const endpoint = file["@sharePoint.endpoint"];

			let token: string;
			let itemPath: string;

			if (isPersonalAccount(account)) {
				token = await this.acquireToken(CONSUMER_SCOPES, false);
				itemPath = `/drive/items/${file.id}`;
			} else {
				// The endpoint is on the SharePoint host of the library the file lives in, which needs its own token.
				// Files from shared / SharePoint libraries live in another drive than the user's own OneDrive.
				token = await this.acquireToken(sharePointScopes(endpoint), false);
				itemPath = `/drives/${file.parentReference.driveId}/items/${file.id}`;
			}

			const response = await axios.get(endpoint + itemPath, {
				headers: {
					"Authorization": "Bearer " + token
				}
			});

			if (response.data.file?.mimeType) {
				return response.data.file.mimeType;
			} else if (response.data.folder) {
				return 'application/vnd.microsoft-365.folder';
			}

			return null;
		},
	},

}
</script>
